import { useState, useEffect } from 'react';
import { CanvasLayoutSelector } from './CanvasLayoutSelector';
import { CanvasPreview } from './CanvasPreview';
import {
  FrameStepCanvasPanel,
  FrameStepPreviewPanel,
  FrameStepFrameListPanel,
} from './FrameCategorySelector';
import { PhotoGallery } from './PhotoGallery';
import { getLayoutsForPaperType, getLayoutById } from '../types/canvas-layouts';
import { loadFrames } from '../../../lib/db/frames';
import { savePhotoToBridge } from '../../../lib/bridge/storage-client';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../../lib/config/terminal-urls';
import { useOutletAuth } from '../../../lib/auth/OutletAuthProvider';
import { useToast } from '../../../components/ToastContainer';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ErrorMessage } from '../../../components/ErrorMessage';
import { renderCanvasToBlob } from '../renderCanvasToBlob';
import type { LocalFrame } from '../../../lib/db/schema';
import type {
  EditSessionPhoto,
  EditSessionProduct,
  EditSessionTransaction,
  PhotoTransform,
} from '../types/edit-session';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

type EditStep = 'layout_photos' | 'frame_selection';

/** Sync auth token to this component's API client so PATCH /print and /edit include Authorization */
function useSyncAuthToken(): void {
  const { authState } = useOutletAuth();
  useEffect(() => {
    try {
      const token =
        localStorage.getItem('photobooth_device_token') ||
        localStorage.getItem('photobooth_auth_token');
      if (token) {
        apiClient.setAuthToken(token);
      }
    } catch {
      // Non-critical if storage is unavailable
    }
  }, [authState]);
}

export interface ThreePanelLayoutProps {
  photos: EditSessionPhoto[];
  product: EditSessionProduct;
  transaction: EditSessionTransaction;
  outletId: string;
  onComplete: () => void;
  onCancel?: () => void;
}

interface ThreePanelLayoutState {
  step: EditStep;
  /** Layout id per canvas (length = printCount) */
  canvasLayoutIds: string[];
  /** Global slotIndex -> photoId */
  photoArrangement: Map<number, string>;
  /** Step 2: per-slot pan/zoom (globalSlotIndex -> PhotoTransform) */
  photoTransforms: Map<number, PhotoTransform>;
  currentCanvasIndex: number;
  /** Step 2: frame selection */
  availableFrames: LocalFrame[];
  /** Map frame category id -> display name from frame categories API */
  frameCategoryNames: Record<string, string>;
  /** Map frame category id -> displayOrder for sorting category rows */
  frameCategoryDisplayOrder: Record<string, number>;
  selectedFrameIds: string[]; // Array of frame IDs, one per canvas (length = printCount)
  framesLoading: boolean;
  framesError: string | null;
  showConfirmDialog: boolean;
  isSaving: boolean;
  saveError: string | null;
}

function getDefaultLayoutId(paperType: 'collage_4x6' | 'strip'): string {
  const layouts = getLayoutsForPaperType(paperType);
  return layouts[0]?.id ?? '';
}

export function ThreePanelLayout({
  photos,
  product,
  transaction,
  onComplete,
  onCancel: _onCancel,
}: ThreePanelLayoutProps) {
  useSyncAuthToken();
  const toast = useToast();
  const printCount = Math.max(1, product.printCount ?? 1);

  const [state, setState] = useState<ThreePanelLayoutState>(() => ({
    step: 'layout_photos',
    canvasLayoutIds: Array.from({ length: printCount }, () =>
      getDefaultLayoutId(product.paperType)
    ),
    photoArrangement: new Map(),
    photoTransforms: new Map(),
    currentCanvasIndex: 0,
    availableFrames: [],
    frameCategoryNames: {},
    frameCategoryDisplayOrder: {},
    selectedFrameIds: Array.from({ length: printCount }, () => ''), // Empty string means no frame selected
    framesLoading: false,
    framesError: null,
    showConfirmDialog: false,
    isSaving: false,
    saveError: null,
  }));

  // Load frames when user moves to step 2 (frame selection)
  useEffect(() => {
    if (state.step !== 'frame_selection') return;

    let cancelled = false;
    async function fetchFrames() {
      setState((prev) => ({ ...prev, framesLoading: true, framesError: null }));
      try {
        const [frames, categoriesResponse] = await Promise.all([
          loadFrames(product.paperType),
          apiClient.frameCategories.getAll(),
        ]);
        if (cancelled) return;
        const frameCategoryNames: Record<string, string> = {};
        const frameCategoryDisplayOrder: Record<string, number> = {};
        if (categoriesResponse.success && categoriesResponse.data) {
          for (const c of categoriesResponse.data) {
            frameCategoryNames[c.frameCategoryId] = c.name;
            frameCategoryDisplayOrder[c.frameCategoryId] = c.displayOrder;
          }
        }
        setState((prev) => ({
          ...prev,
          availableFrames: frames,
          frameCategoryNames,
          frameCategoryDisplayOrder,
          framesLoading: false,
          framesError: null,
        }));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load frames:', err);
        const msg =
          err instanceof Error
            ? err.message
            : 'Unable to load frames. Please check your connection and try again.';
        setState((prev) => ({
          ...prev,
          framesLoading: false,
          framesError: msg,
        }));
      }
    }
    fetchFrames();
    return () => {
      cancelled = true;
    };
  }, [state.step, product.paperType]);

  const handleLayoutSelect = (layoutId: string) => {
    setState((prev) => {
      const next = [...prev.canvasLayoutIds];
      next[prev.currentCanvasIndex] = layoutId;
      return { ...prev, canvasLayoutIds: next };
    });
  };

  const handlePhotoDrop = (slotIndex: number, photoId: string) => {
    setState((prev) => {
      const next = new Map(prev.photoArrangement);
      next.set(slotIndex, photoId);
      return { ...prev, photoArrangement: next };
    });
  };

  const handleDropComplete = (slotIndex: number, _photoId: string, replaced: boolean) => {
    if (replaced) {
      toast.showSuccess(`Photo replaced in slot ${slotIndex + 1}`, 2000);
    } else {
      toast.showSuccess(`Photo placed in slot ${slotIndex + 1}`, 2000);
    }
  };

  const handleInvalidDrop = () => {
    toast.showWarning('Please drop the photo into a slot', 2000);
  };

  const handleDragStart = (_photoId: string) => {}
  const handleDragEnd = (_photoId: string, didDrop: boolean) => {
    if (!didDrop) handleInvalidDrop();
  };

  const handleCanvasChange = (canvasIndex: number) => {
    setState((prev) => ({ ...prev, currentCanvasIndex: canvasIndex }));
  };

  /** Total required slots = sum of slot counts for each canvas's layout */
  const getTotalRequiredSlots = (): number => {
    let total = 0;
    for (let i = 0; i < state.canvasLayoutIds.length; i++) {
      const layoutId = state.canvasLayoutIds[i];
      if (!layoutId) continue;
      const layout = getLayoutById(layoutId);
      total += layout?.slots.length ?? 0;
    }
    return total;
  };

  const isLayoutStepComplete = (): boolean => {
    const total = getTotalRequiredSlots();
    for (let i = 0; i < total; i++) {
      if (!state.photoArrangement.has(i)) return false;
    }
    return true;
  };

  /** For each canvas, true if all its photo slots are filled (for tab check overlay). */
  const getCanvasSlotsFilled = (): boolean[] => {
    return state.canvasLayoutIds.map((layoutId, canvasIndex) => {
      const layout = layoutId ? getLayoutById(layoutId) : undefined;
      if (!layout) return false;
      let slotOffset = 0;
      for (let j = 0; j < canvasIndex; j++) {
        const prevId = state.canvasLayoutIds[j];
        const prevLayout = prevId ? getLayoutById(prevId) : undefined;
        slotOffset += prevLayout?.slots.length ?? 0;
      }
      for (let k = 0; k < layout.slots.length; k++) {
        if (!state.photoArrangement.has(slotOffset + k)) return false;
      }
      return true;
    });
  };

  const handleNextToFrameSelection = () => {
    if (!isLayoutStepComplete()) {
      const total = getTotalRequiredSlots();
      const msg = `Please fill all ${total} photo slots before continuing.`;
      setState((prev) => ({ ...prev, saveError: msg }));
      toast.showError(msg, 4000);
      return;
    }
    setState((prev) => {
      // Default to first canvas that doesn't have a frame yet
      const firstWithoutFrame = prev.selectedFrameIds.findIndex((id) => !id || id === '');
      const nextCanvasIndex = firstWithoutFrame >= 0 ? firstWithoutFrame : 0;
      return {
        ...prev,
        step: 'frame_selection',
        currentCanvasIndex: nextCanvasIndex,
        saveError: null,
      };
    });
  };

  const handleBackToLayout = () => {
    setState((prev) => ({
      ...prev,
      step: 'layout_photos',
      framesError: null,
    }));
  };

  const handleFrameSelect = (frameId: string, canvasIndex: number) => {
    setState((prev) => {
      const newSelectedFrameIds = [...prev.selectedFrameIds];
      newSelectedFrameIds[canvasIndex] = frameId;
      return { ...prev, selectedFrameIds: newSelectedFrameIds };
    });
  };

  const handleTransformChange = (globalSlotIndex: number, transform: PhotoTransform) => {
    setState((prev) => {
      const next = new Map(prev.photoTransforms);
      next.set(globalSlotIndex, transform);
      return { ...prev, photoTransforms: next };
    });
  };

  const handleComplete = () => {
    setState((prev) => ({
      ...prev,
      showConfirmDialog: true,
      saveError: null,
    }));
  };

  const handleConfirmComplete = async () => {
    setState((prev) => ({ ...prev, isSaving: true, saveError: null }));

    try {
      const photosForRender = photos.map((p) => ({ photoId: p.photoId, imageBlob: p.imageBlob }));

      for (let i = 0; i < printCount; i++) {
        const layoutId = state.canvasLayoutIds[i];
        if (!layoutId) continue;
        const layout = getLayoutById(layoutId);
        if (!layout) continue;

        // Get frame for this specific canvas
        const frameId = state.selectedFrameIds[i];
        const frame = frameId
          ? state.availableFrames.find((f) => f.frameId === frameId) ?? null
          : null;

        let slotOffset = 0;
        for (let j = 0; j < i; j++) {
          const prevId = state.canvasLayoutIds[j];
          const prevLayout = prevId ? getLayoutById(prevId) : undefined;
          slotOffset += prevLayout?.slots.length ?? 0;
        }

        const slotPhotoIds = layout.slots
          .map((_, j) => state.photoArrangement.get(slotOffset + j) ?? '')
          .filter(Boolean);
        if (slotPhotoIds.length !== layout.slots.length) {
          throw new Error(`Photo ${i + 1}: not all slots are filled.`);
        }

        const canvasTransforms = new Map<number, PhotoTransform>();
        for (let j = 0; j < layout.slots.length; j++) {
          const t = state.photoTransforms.get(slotOffset + j);
          if (t) canvasTransforms.set(j, t);
        }

        // Output dimensions by paper type and orientation (4x6 at ~571 DPI, strip 2x6 at 300 DPI)
        const outputSize =
          layout.paperType === 'collage_4x6'
            ? layout.orientation === 'landscape'
              ? { width: 5139, height: 3426 }
              : { width: 3426, height: 5139 }
            : layout.paperType === 'strip'
              ? { width: 600, height: 1800 }
              : undefined;

        const blob = await renderCanvasToBlob(
          layout,
          slotPhotoIds,
          photosForRender,
          frame ? { imageBlob: frame.imageBlob } : null,
          { transforms: canvasTransforms, ...outputSize }
        );
        await savePhotoToBridge(
          blob,
          transaction.transactionId,
          transaction.customerName,
          i + 1,
          true // isEdited = true for edited photos
        );
      }

      await apiClient.transactions.updateEditStatus(transaction.transactionId, 'completed');
      await apiClient.transactions.updatePrintStatus(transaction.transactionId, 'queued');
      setState((prev) => ({
        ...prev,
        isSaving: false,
        showConfirmDialog: false,
      }));
      onComplete();
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to send photos. Please try again.';
      setState((prev) => ({
        ...prev,
        isSaving: false,
        saveError: msg,
      }));
      toast.showError(msg, 5000);
    }
  };

  const handleCancelConfirm = () => {
    setState((prev) => ({
      ...prev,
      showConfirmDialog: false,
      saveError: null,
    }));
  };

  const handleRetryFrames = () => {
    setState((prev) => ({ ...prev, framesLoading: true, framesError: null }));
    window.location.reload();
  };

  // Slot offset for current canvas (sum of slot counts of previous canvases)
  let slotOffsetForCurrentCanvas = 0;
  for (let i = 0; i < state.currentCanvasIndex; i++) {
    const layoutId = state.canvasLayoutIds[i];
    const layout = layoutId ? getLayoutById(layoutId) : undefined;
    slotOffsetForCurrentCanvas += layout?.slots.length ?? 0;
  }

  const currentLayoutId = state.canvasLayoutIds[state.currentCanvasIndex] ?? null;

  // Step 1: "Next: Choose Frame" enabled only when every canvas has a layout and every slot has a photo
  const step1CanProceed = getTotalRequiredSlots() > 0 && isLayoutStepComplete();

  // --- Step 2: Frame selection view
  if (state.step === 'frame_selection') {
    if (state.framesLoading) {
      return (
        <div className="flex items-center justify-center min-h-[600px] bg-white rounded-3xl shadow-xl p-12">
          <LoadingSpinner size="xl" message="Loading frames..." />
        </div>
      );
    }
    if (state.framesError) {
      return (
        <ErrorMessage
          title="Error Loading Frames"
          message={state.framesError}
          onRetry={handleRetryFrames}
          onCancel={onComplete}
          fullScreen={true}
        />
      );
    }
    const filteredFrames = state.availableFrames.filter((f) => f.paperType === product.paperType);
    const currentSelectedFrameId = state.selectedFrameIds[state.currentCanvasIndex] ?? '';
    const selectedFrame =
      currentSelectedFrameId ?
        filteredFrames.find((f) => f.frameId === currentSelectedFrameId) ?? null
      : null;
    const currentLayoutIdForPreview = state.canvasLayoutIds[state.currentCanvasIndex];

    return (
      <div className="edit-step2-root bg-white rounded-3xl shadow-xl overflow-hidden" role="main" aria-label="Frame selection">
        <div className="three-panel-layout">
          <div className="left-panel" role="region" aria-label="Photo selection">
            <FrameStepCanvasPanel
              printCount={printCount}
              currentCanvasIndex={state.currentCanvasIndex}
              selectedFrameIds={state.selectedFrameIds}
              onCanvasChange={handleCanvasChange}
              frameCount={filteredFrames.length}
            />
          </div>
          <div className="center-panel" role="region" aria-label="Frame preview">
            <FrameStepPreviewPanel
              selectedFrame={selectedFrame}
              currentCanvasIndex={state.currentCanvasIndex}
              layoutId={currentLayoutIdForPreview}
              slotOffset={slotOffsetForCurrentCanvas}
              photoArrangement={state.photoArrangement}
              photos={photos}
              paperType={product.paperType}
              photoTransforms={state.photoTransforms}
              onTransformChange={handleTransformChange}
            />
          </div>
          <div className="right-panel" role="region" aria-label="Frame list">
            <FrameStepFrameListPanel
              frames={filteredFrames}
              frameCategoryNames={state.frameCategoryNames}
              frameCategoryDisplayOrder={state.frameCategoryDisplayOrder}
              paperType={product.paperType}
              currentSelectedFrameId={currentSelectedFrameId}
              currentCanvasIndex={state.currentCanvasIndex}
              onFrameSelect={handleFrameSelect}
            />
          </div>
        </div>
        <div className="border-t border-gray-200 p-6 flex items-center justify-between flex-shrink-0">
          <div className="flex-1">
            {state.saveError && (
              <div className="text-red-600 text-sm font-medium" role="alert">
                {state.saveError}
              </div>
            )}
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleBackToLayout}
              disabled={state.isSaving}
              className="px-8 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-all duration-200 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-gray-400"
              aria-label="Back to layout and photos"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleComplete}
              disabled={state.isSaving}
              className="px-8 py-3 rounded-xl bg-yellow-500 text-white font-semibold hover:bg-yellow-600 transition-all duration-200 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-yellow-400"
              aria-label="Complete and save"
            >
              Complete
            </button>
          </div>
        </div>

        {/* Confirmation dialog (same as before) */}
        {state.showConfirmDialog && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4" aria-hidden>✅</div>
                <h3 id="confirm-dialog-title" className="text-2xl font-bold mb-2 text-gray-900">
                  Confirm Photo Arrangement
                </h3>
                <p id="confirm-dialog-description" className="text-gray-600">
                  Are you ready to proceed? This will finalize your layout and frame selection. This action cannot be undone.
                </p>
              </div>
              {state.saveError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                  <p className="text-red-600 text-sm font-medium">{state.saveError}</p>
                </div>
              )}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleCancelConfirm}
                  disabled={state.isSaving}
                  className="flex-1 px-6 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-gray-400"
                  aria-label="Go back"
                >
                  Go Back
                </button>
                <button
                  type="button"
                  onClick={handleConfirmComplete}
                  disabled={state.isSaving}
                  className="flex-1 px-6 py-3 rounded-xl bg-yellow-500 text-white font-semibold hover:bg-yellow-600 disabled:opacity-50 flex items-center justify-center gap-2 focus:outline-none focus:ring-4 focus:ring-yellow-400"
                  aria-label="Confirm and save"
                >
                  {state.isSaving ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Confirm'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <style>{`
          .edit-step2-root {
            display: flex;
            flex-direction: column;
            min-height: 500px;
            height: calc(100vh - 200px);
            width: 100%;
          }
          .edit-step2-root .three-panel-layout {
            display: grid;
            grid-template-columns: 1fr 2fr 1fr;
            gap: 1.5rem;
            flex: 1 1 0;
            min-height: 0;
            min-width: 0;
            padding: 0.5rem;
          }
          .edit-step2-root .left-panel,
          .edit-step2-root .center-panel,
          .edit-step2-root .right-panel {
            min-height: 0;
            min-width: 0;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 1.5rem;
            border-right: 2px solid #e5e7eb;
            display: flex;
            flex-direction: column;
          }
          .edit-step2-root .right-panel { 
            overflow-y: auto;
            overflow-x: hidden;
          }
          @media (max-width: 1024px) {
            .edit-step2-root { max-height: none; }
            .edit-step2-root .three-panel-layout {
              grid-template-columns: 1fr;
              grid-template-rows: auto auto auto;
              gap: 1rem;
            }
            .edit-step2-root .left-panel,
            .edit-step2-root .center-panel,
            .edit-step2-root .right-panel {
              border-right: none;
              border-bottom: 2px solid #e5e7eb;
              max-height: 400px;
              min-height: 0;
              padding: 1rem;
            }
            .edit-step2-root .right-panel { border-bottom: none; }
          }
        `}</style>
      </div>
    );
  }

  // --- Step 1: Layout + Photos (three-panel)
  return (
    <div className="edit-step1-root bg-white rounded-3xl shadow-xl overflow-hidden" role="main" aria-label="Photo layout editor">
      <div className="three-panel-layout">
        <div className="left-panel" role="region" aria-label="Photo layout selection">
          <CanvasLayoutSelector
            paperType={product.paperType}
            selectedLayoutId={currentLayoutId}
            onLayoutSelect={handleLayoutSelect}
          />
        </div>

        <div className="center-panel" role="region" aria-label="Photo preview">
          <CanvasPreview
            canvasCount={printCount}
            currentCanvasIndex={state.currentCanvasIndex}
            currentLayoutId={currentLayoutId}
            slotOffsetForCurrentCanvas={slotOffsetForCurrentCanvas}
            photoArrangement={state.photoArrangement}
            photos={photos}
            onCanvasChange={handleCanvasChange}
            onPhotoDropped={handlePhotoDrop}
            onDropComplete={handleDropComplete}
            canvasSlotsFilled={getCanvasSlotsFilled()}
          />
        </div>

        <div className="right-panel" role="region" aria-label="Photo gallery">
          <PhotoGallery
            photos={photos}
            photoArrangement={state.photoArrangement}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        </div>
      </div>

      <div className="edit-step1-actions border-t border-gray-200 p-6 flex items-center justify-between flex-shrink-0" role="region" aria-label="Actions">
        <div className="flex-1">
          {state.saveError && (
            <div className="text-red-600 text-sm font-medium" role="alert">
              {state.saveError}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleNextToFrameSelection}
            disabled={state.isSaving || !step1CanProceed}
            className="px-8 py-3 rounded-xl bg-yellow-500 text-white font-semibold hover:bg-yellow-600 transition-all duration-200 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-yellow-400"
            aria-label="Next: choose frame"
          >
            Next: Choose Frame
          </button>
        </div>
      </div>

      <style>{`
        .edit-step1-root {
          display: flex;
          flex-direction: column;
          min-height: 500px;
          height: calc(100vh - 200px);
          width: 100%;
        }
        .three-panel-layout {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 1.5rem;
          flex: 1 1 0;
          min-height: 0;
          min-width: 0;
          padding: 0.5rem;
        }
        .left-panel {
          min-height: 0;
          min-width: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 1.5rem;
          border-right: 2px solid #e5e7eb;
          display: flex;
          flex-direction: column;
        }
        .center-panel {
          display: flex;
          flex-direction: column;
          min-height: 0;
          min-width: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 1.5rem;
          border-right: 2px solid #e5e7eb;
        }
        .right-panel {
          min-height: 0;
          min-width: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
        }
        @media (max-width: 1024px) {
          .edit-step1-root { max-height: none; }
          .three-panel-layout {
            grid-template-columns: 1fr;
            grid-template-rows: auto auto auto;
            gap: 1rem;
          }
          .left-panel, .center-panel, .right-panel {
            border-right: none;
            border-bottom: 2px solid #e5e7eb;
            max-height: 400px;
            min-height: 0;
            padding: 1rem;
          }
          .right-panel { border-bottom: none; }
        }
        .left-panel [data-testid="canvas-layout-list"],
        .right-panel [data-testid="photo-gallery-scroll-container"] {
          overflow-x: hidden;
          scroll-behavior: smooth;
          scrollbar-width: thin;
          scrollbar-color: #d1d5db #f3f4f6;
        }
        .left-panel [data-testid="canvas-layout-list"]::-webkit-scrollbar,
        .right-panel [data-testid="photo-gallery-scroll-container"]::-webkit-scrollbar { width: 6px; }
        .left-panel [data-testid="canvas-layout-list"]::-webkit-scrollbar-track,
        .right-panel [data-testid="photo-gallery-scroll-container"]::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 3px; }
        .left-panel [data-testid="canvas-layout-list"]::-webkit-scrollbar-thumb,
        .right-panel [data-testid="photo-gallery-scroll-container"]::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
        .left-panel [data-testid="canvas-layout-list"]::-webkit-scrollbar-thumb:hover,
        .right-panel [data-testid="photo-gallery-scroll-container"]::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
        *:focus-visible { outline: 2px solid #eab308; outline-offset: 2px; }
      `}</style>
    </div>
  );
}
